import { Plugin, TemplateExpressionShape } from '@servicenow/sdk-build-core'
import type { Compiler, Diagnostics, Shape } from '@servicenow/sdk-build-core'
import type { Project } from '@servicenow/sdk-build-core'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import { DECISION_ACTIVITY_DEF_KEY } from '../constants/activity-ootb-maps'
import { ActivityShape, getFactoryCallConfig } from '../utils/playbook-shapes'
import { validateDependentFields } from '../utils/dependent-field-validator'
import { HTML_TAG_REGEX, stripHtmlTags } from '../utils/label-utils'

/**
 * Validates Activity calls and emits diagnostics for invalid configurations.
 *
 * Runs for every Activity and validates:
 * - Dependent field references (FieldListColumn, TemplateValueColumn) against
 *   the resolved table schema
 * - Decision-specific branch validation (for Decision activities only)
 *
 * Note: This plugin returns { success: false } to indicate it is for diagnostics only
 * and does not create records.
 */
export const ActivityDiagnosticsPlugin = Plugin.create({
    name: 'ActivityDiagnosticsPlugin',
    docs: [],
    shapes: [
        {
            shape: ActivityShape,
            fileTypes: ['fluent'],

            toRecord(
                activityShape: ActivityShape,
                { diagnostics, compiler, project }: { diagnostics: Diagnostics; compiler: Compiler; project: Project }
            ) {
                const isDecision = activityShape.getActivityDefinition().getCode() === DECISION_ACTIVITY_DEF_KEY
                const config = activityShape.getConfig()

                // Decision activities express their conditions via branches; the TS overload
                // omits `conditionToRun`, but JS/`any`/spread-from-untyped callers can bypass
                // that. Mirror the constraint at the diagnostics layer.
                if (isDecision) {
                    const conditionToRunShape = config.get('conditionToRun')
                    if (conditionToRunShape.ifDefined() !== undefined) {
                        diagnostics.error(conditionToRunShape, 'Decision activities must not define `conditionToRun`.')
                    }
                }

                // Validation: label must not be empty/whitespace, and must not contain HTML.
                //
                // - HTML: platform BR "Strip HTML from label" (sys_script_de69da9241f33010f8773a54b5d2b3a3)
                //   silently strips HTML on insert/update. ActivityPlugin's toRecord transform
                //   performs the same strip; this warning surfaces it to the developer.
                // - Whitespace: the PAD UI and the platform's Jelly layer reject whitespace-only
                //   labels, so persisting one would fail at runtime. We error early. The check is
                //   applied after stripping HTML so that labels like '<b>   </b>' (which the BR
                //   collapses to '   ') are also flagged.
                const labelShape = config.get('label')?.ifString()
                const labelValue = labelShape?.getValue()
                if (labelValue !== undefined) {
                    const labelSource = labelShape ?? config.get('label') ?? config
                    if (HTML_TAG_REGEX.test(labelValue)) {
                        diagnostics.warn(
                            labelSource,
                            'Activity `label` contains HTML which is not supported. HTML will be stripped from the label.'
                        )
                    }
                    if (stripHtmlTags(labelValue).trim() === '') {
                        diagnostics.error(labelSource, 'Activity `label` cannot be empty or contain only whitespace.')
                    }
                }

                // ──────────────────────────────────────────────────────────
                // Dependent field validation (all activity types)
                // ──────────────────────────────────────────────────────────

                // Resolve the activity definition to access the column schemas
                // for the backing action/subflow inputs and activity type
                // experience properties. These schemas tell us which columns
                // have `dependent` relationships that need validation.
                const activityDefConfig = getFactoryCallConfig(activityShape.getActivityDefinition())
                if (activityDefConfig) {
                    const rootDir = project.getRootDir()

                    // Validate experience properties (from the ActivityType)
                    const activityTypeConfig = getFactoryCallConfig(activityDefConfig.get('activityType'))
                    const backingExperienceProperties = activityTypeConfig
                        ?.get('experienceProperties', false)
                        ?.ifObject()
                    const experienceProperties = activityShape.getExperienceProperties()
                    if (backingExperienceProperties && experienceProperties) {
                        validateDependentFields(
                            backingExperienceProperties,
                            experienceProperties,
                            compiler,
                            rootDir,
                            diagnostics
                        )
                    }

                    // Validate inputs (from the backing Action or Subflow)
                    const flowShape = activityDefConfig.get('flow')
                    const actionShape = activityDefConfig.get('action')
                    const backingShape = flowShape?.ifDefined() ?? actionShape
                    const backingConfig = getFactoryCallConfig(backingShape)
                    const backingInputs = backingConfig?.get('inputs', false)?.ifObject()
                    const inputs = activityShape.getInputs()
                    if (backingInputs && inputs) {
                        validateDependentFields(backingInputs, inputs, compiler, rootDir, diagnostics)
                    }
                }

                // ──────────────────────────────────────────────────────────
                // Decision validation
                // ──────────────────────────────────────────────────────────

                if (!isDecision) {
                    return { success: false }
                }

                // Get branches from inputs
                const inputs = activityShape.getInputs()
                const branchesRaw = inputs?.get('branches')
                if (!branchesRaw || branchesRaw.isUndefined()) {
                    diagnostics.error(activityShape, 'Decision activity requires a branches array in inputs')
                    return { success: false }
                }
                const branchesShape = branchesRaw.ifArray()
                if (!branchesShape) {
                    diagnostics.error(branchesRaw, 'Decision branches must be an array')
                    return { success: false }
                }

                const branchElements = branchesShape.getElements()

                if (branchElements.length < 2) {
                    diagnostics.error(branchesShape, 'Decision activity must have at least 2 branches')
                    return { success: false }
                }

                // Validate: must have at least one non-else branch
                const hasNonElseBranch = branchElements.some((elem) => {
                    const id = elem.ifObject()?.get('id')?.ifString()?.getValue()
                    return id !== 'else'
                })
                if (!hasNonElseBranch) {
                    diagnostics.error(
                        branchesShape,
                        'Decision activity must have at least one conditional branch (not just else)'
                    )
                }

                // Validate each branch
                const seenIds = new Set<string>()
                for (const elem of branchElements) {
                    const branch = elem.ifObject()
                    if (!branch) {
                        continue
                    }

                    const idShape = branch.get('id')?.ifString()
                    const id = idShape?.getValue()
                    const isElse = id === 'else'

                    // Validate: duplicate IDs
                    if (id !== undefined) {
                        if (seenIds.has(id)) {
                            diagnostics.error(
                                idShape ?? branch,
                                `Duplicate branch id '${id}': branch ids must be unique`
                            )
                        } else {
                            seenIds.add(id)
                        }
                    }

                    // Validate: else branch with condition
                    if (isElse) {
                        const conditionShape = branch.get('condition')
                        const hasCondition = conditionShape?.ifString() || conditionShape?.if(TemplateExpressionShape)
                        if (hasCondition) {
                            diagnostics.hint(
                                conditionShape!,
                                `Else branch should not have a condition — it always matches`
                            )
                        }
                    }
                }

                // Validate: else branch is required and must be the last branch
                const lastBranch = branchElements[branchElements.length - 1]?.ifObject()
                const lastBranchIsElse = lastBranch?.get('id')?.ifString()?.getValue() === 'else'
                const hasElse = branchElements.some(
                    (elem) => elem.ifObject()?.get('id')?.ifString()?.getValue() === 'else'
                )
                if (!hasElse) {
                    diagnostics.error(branchesShape, 'Decision activity must have an else branch as the last element')
                } else if (!lastBranchIsElse) {
                    diagnostics.error(branchesShape, 'Else branch must be the last branch in the array')
                }

                // Validate: conditions must only reference prior activities (not forward or parallel)
                // nowIdKeyToGuid is built incrementally by processChildCollectionToRecord,
                // so it only contains activities processed before this one.
                const nowIdKeyToGuid = activityShape.getNowIdKeyToGuid()
                if (nowIdKeyToGuid) {
                    for (const elem of branchElements) {
                        const branch = elem.ifObject()
                        if (!branch) {
                            continue
                        }
                        const conditionShape = branch.get('condition')
                        const templateExpr = conditionShape?.if(TemplateExpressionShape)
                        if (!templateExpr) {
                            continue
                        }

                        templateExpr.getValue((expression: Shape) => {
                            const resolved = expression.isResolvable()
                                ? expression.asResolvable().resolve()
                                : expression
                            if (resolved.is(NowIdShape)) {
                                const key = String(resolved.as(NowIdShape).getValue())
                                if (!nowIdKeyToGuid.has(key)) {
                                    const branchId = branch.get('id')?.ifString()?.getValue() ?? '(unknown)'
                                    diagnostics.error(
                                        conditionShape!,
                                        `Branch '${branchId}' condition references '${key}' which is not a prior activity. ` +
                                            `Conditions can only reference activities defined before this Decision.`
                                    )
                                }
                            }
                            return '' // return value unused — inspecting only
                        })
                    }
                }

                return { success: false }
            },
        },
    ],
})
